import { gql, useMutation } from '@apollo/client';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useRemoveCartItem } from '@app/hooks/useRemoveCartItem';
import { CART_UPDATE_RESULT_FRAGMENT } from '@app/pages/Cart/Cart.queries';
import type {
  CartQuery_magento2,
  CartQuery_magento2_cart,
  UpdateCartItemsMutation,
  UpdateCartItemsMutationVariables,
} from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';

import { pushPerpetualEcommerceEvent } from '../../../helpers/tracking';
import CartItem from './CartItem/CartItem';
import messages from './CartItems.messages';
import { ColumnHeader } from './CartItems.styles';

interface CartItemsProps {
  cartId?: string;
  items?: CartQuery_magento2_cart['items'];
  storeConfig?: CartQuery_magento2['storeConfig'];
}

const CartItems = (props: CartItemsProps) => {
  const { removeItem } = useRemoveCartItem();
  const { cartId, items, storeConfig } = props;

  const [updateCartItems] = useMutation<
    UpdateCartItemsMutation,
    UpdateCartItemsMutationVariables
  >(UPDATE_CART_ITEMS_MUTATION);

  const setItemQuantity = useCallback<
    (id: string, quantity: number) => Promise<void>
  >(
    async (id, quantity) => {
      if (!cartId) return;

      await updateCartItems({
        variables: {
          cartId,
          cartItems: [{ quantity, cart_item_uid: id }],
        },
      });
    },
    [cartId, updateCartItems],
  );

  const handleRemoveItem = (itemUid) => {
    const removingProduct = items?.find((item) => item?.uid === itemUid);
    const { product, prices, quantity } = removingProduct || {};
    const { value: price, currency } = prices?.rowTotalIncludingTax || {};

    pushPerpetualEcommerceEvent('remove_from_cart', {
      currency: currency || '',
      value: price || 0,
      items: [
        {
          // item_id/item_name should come from the parent product,
          // but it's not available in the cart item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: product?.sku || '',
          item_name: product?.name || '',
          sku_id: product?.sku || '',
          sku_name: product?.name || '',
          sdbs_id: product?.sdbsId,
          price: price || 0,
          quantity,
        },
      ],
    });

    removeItem(itemUid);
  };

  return (
    <>
      {/* needed to maintain grid layout */}
      <ColumnHeader />
      <ColumnHeader>
        <FormattedMessage {...messages.product} />
      </ColumnHeader>
      <ColumnHeader>
        <FormattedMessage {...messages.price} />
      </ColumnHeader>
      <ColumnHeader>
        <FormattedMessage {...messages.quantity} />
      </ColumnHeader>
      <ColumnHeader hAlign="right">
        <FormattedMessage {...messages.total} />
      </ColumnHeader>
      {items?.map(
        (item) =>
          item && (
            <CartItem
              item={item}
              onQuantitySelect={setItemQuantity}
              onRemove={handleRemoveItem}
              key={item.uid}
              includingTax={storeConfig?.taxSalesDisplayPrice === 'including'}
            />
          ),
      )}
    </>
  );
};

export default memo(CartItems);

export const UPDATE_CART_ITEMS_MUTATION = gql`
  ${CART_UPDATE_RESULT_FRAGMENT}

  mutation UpdateCartItemsMutation(
    $cartId: String!
    $cartItems: [Magento2_CartItemUpdateInput!]!
  ) {
    magento2 {
      updateCartItems(input: { cart_id: $cartId, cart_items: $cartItems }) {
        cart {
          ...CartUpdateResult
        }
      }
    }
  }
`;
